import { FormEvent, Fragment, FunctionComponent, useState } from 'react';
import { useCategoriesOfferContext } from '../context/CategoriesOfferContext';
import SummaryDetails from './SummaryDetails';
import { sendEstimate, updateOrder } from '../api';
import '../styles/components/summary.scss';
import Noty from 'noty';
import 'noty/lib/noty.css';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const Summary: FunctionComponent<{
  customer: string;
  validate: () => boolean;
}> = ({ customer, validate }) => {
  const {
    estimates,
    rotAvdrag,
    toggleRotAvdrag,
    getCost,
    orderId,
    editMode,
    roomInfo,
    wpId,
    loading,
    setLoading,
    setFinished,
    finished,
    startFee,
  } = useCategoriesOfferContext();
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [error, setError] = useState<undefined | string>();
  const [phoneError, setPhoneError] = useState<undefined | string>();

  const reset = () => {
    window.history.scrollRestoration = 'manual';
    window.location.reload();
  };

  const send = async (e: any) => {
    e.preventDefault();
    setError(undefined);
    setPhoneError(undefined);

    if (!validate()) {
      return;
    }

    if (!phone) {
      setPhoneError('Vänligen fyll i ditt telefonnummer');
      return;
    } else if (new RegExp(/^[0-9-+]+$/g).test(phone) === false) {
      setPhoneError('Vänligen fyll i ett giltigt telefonnummer');
      return;
    }

    if (!email) {
      setError('Vänligen fyll i din e-post');
      return;
    } else if (
      // eslint-disable-next-line no-useless-escape
      new RegExp(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g).test(email) === false
    ) {
      setError('Vänligen fyll i en giltig e-post');
      return;
    }

    if (orderId && customer) {
      try {
        const responseLaravel = await updateOrder({
          email,
          phone,
          orderId,
          rot: rotAvdrag,
          estimates: estimates.map((e) => ({
            category: e[1].category,
            id: e[1].job_id,
          })),
        });
        if (responseLaravel === false) {
          setError('could not save');
        }
        const response = await sendEstimate({
          customer,
          name: `Estimate: ${email}`,
          orderId,
          price: getCost().finalCost,
          data: JSON.stringify(estimates, null, '\t'),
        });
        if (response.status !== 200) {
          setError(response.message || 'Det blev fel');
        } else if (response.post_id) {
          try {
            await updateOrder({
              orderId,
              wpId: response.post_id,
            });
            setFinished(true);

            if (window.dataLayer) {
              window.dataLayer.push({
                event: 'form_submit',
                form_id:
                  document
                    .querySelector('.page-template')
                    ?.className.match(/page-id-(\d+)/)?.[1] || null,
              });
            } else {
              console.error('dataLayer is not initialized.');
            }
          } catch (e) {
            setError(e.message);
          }
        }
      } catch (e) {
        setError(e.message);
      }
    } else {
      if (!orderId) {
        setError('Vänligen se över att du fyllt i alla fält');
      } else {
        setError('Vi kunde inte skicka din offert, försök igen senare');
      }
    }
  };

  const save = async (e: any) => {
    e.preventDefault();
    setError(undefined);
    if (orderId) {
      try {
        const res = await updateOrder({
          rot: rotAvdrag,
          roomInfo,
          orderId,
          estimates: estimates.map((e) => ({
            category: e[1].category,
            id: e[1].job_id,
          })),
        });
        if (typeof res !== 'boolean') {
          setError(res?.message || 'could not save');
        } else {
          const response = await sendEstimate({
            customer,
            name: `Estimate: ${email}`,
            orderId,
            price: getCost().finalCost,
            data: JSON.stringify(estimates, null, '\t'),
            id: wpId,
          });
          if (response.status !== 200) {
            setError(response.message || 'Det blev fel');
          } else {
            new Noty({
              text: 'Sparat!',
              type: 'success',
              timeout: 2000,
              progressBar: false,
            }).show();
          }
        }
      } catch (e) {
        setError(e.message);
      }
    }
  };

  const triggerSend = async (e: any) => {
    setLoading(true);
    await send(e);
    setLoading(false);
  };

  const triggerSave = async (e: any) => {
    setLoading(true);
    await save(e);
    setLoading(false);
  };

  return (
    <div className="summary_card w-full">
      {finished && (
        <div className="calculator_finished">
          <div className="calculator_modal">
            <h2 className="text-center">Tack för din prisförfrågan!</h2>
            <p className="text-center">
              Du kommer inom kort få ett mail med din offert. Vi kommer att gå
              igenom din förfrågan och återkomma till dig så snart som möjligt.
            </p>
            <button className="primary_button" onClick={reset}>
              Stäng
            </button>
          </div>
        </div>
      )}
      <div className="summary_list">
        <h2 className="text-center">Summering</h2>
        {estimates.length === 0 ? (
          <>
            <p className="text-center">
              Prisuträkningen är inte tillgänglig eftersom du inte fyllt i alla
              uppgifter
            </p>
            <button onClick={triggerSend}>Ange uppgifter</button>
          </>
        ) : null}
        {estimates.map(([categoryKey, offer]) => (
          <div className="summary_list_wrapper" key={categoryKey}>
            <h3 className="summary_list_label">{categoryKey}</h3>
            <SummaryDetails category={categoryKey} offert={offer} />
          </div>
        ))}
        {startFee && (
          <div className="summaryDetails_description">
            <h2>Startavgift</h2>
            <h2>{startFee.toLocaleString('sv-SE')}kr</h2>
          </div>
        )}
        {getCost().finalCost ? (
          <div className="summary_list_rot">
            <label className="summary_list_rot_toggle">
              <input
                type="checkbox"
                checked={rotAvdrag}
                onChange={toggleRotAvdrag}
              />
              <span className="summary_list_rot_slider"></span>
            </label>
            <span className="summary_list_rot_label">ROT Avdrag: </span>
            <span className="summary_list_rot_amount">
              (-{getCost().rotAmount.toLocaleString('sv-SE')}kr)
            </span>
          </div>
        ) : null}

        {getCost().finalCost ? (
          <h2 className="summary_total_price text-center">
            Totalt preliminärt pris:{' '}
            {((startFee || 0) + getCost().finalCost).toLocaleString('sv-SE')}
            kr
          </h2>
        ) : null}
      </div>
      <div className="summary_send">
        {editMode ? (
          <Fragment>
            <div className="summary_save_label">
              Spara ändringar, order #{orderId}
            </div>
            <div className="summary_send_wrapper">
              <div className="summary_send_email_save_wrapper">
                <button
                  type="button"
                  onClick={triggerSave}
                  className={`primary_button summary_send_email_save ${
                    loading ? 'button_sending' : ''
                  }`}
                >
                  {loading ? 'Sparar...' : 'Spara'}
                </button>
              </div>
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div className="summary_send_wrapper">
              <div className="summary_send_phone">
                <label>Telefonnummer</label>
                <input
                  onChange={(e: FormEvent<HTMLInputElement>) =>
                    setPhone(e.currentTarget.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      triggerSend(e);
                    }
                  }}
                  type="tel"
                  className="summary_send_phone_input"
                  placeholder="Ditt telefonnummer"
                ></input>
                {phoneError && (
                  <span className="calculator_error">{phoneError}</span>
                )}
              </div>
              <div className="summary_send_email">
                <label>E-post</label>
                <input
                  onChange={(e: FormEvent<HTMLInputElement>) =>
                    setEmail(e.currentTarget.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      triggerSend(e);
                    }
                  }}
                  type="email"
                  className="summary_send_email_input"
                  placeholder="Din e-post"
                ></input>
                {error && <span className="calculator_error">{error}</span>}
              </div>
              <button
                type="button"
                onClick={triggerSend}
                className={`send_button ${loading ? 'button_sending' : ''}`}
              >
                <div className="container">
                  <div className="bar"></div>
                </div>
                {loading ? 'Skickar...' : 'Skicka till mig'}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2ZM12 11H8V13H12V16L16 12L12 8V11Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
          </Fragment>
        )}
      </div>
      <div className="summary_terms">
        <div className="mb-2">
          <div>
            <img
              src="https://painting-frontend.vercel.app/check.svg"
              alt="check"
              className="calculator_paint_radio_svg_active"
            />
            Professionella målare
          </div>
          <div>
            <img
              src="https://painting-frontend.vercel.app/check.svg"
              alt="check"
              className="calculator_paint_radio_svg_active"
            />
            Snabb leveranstid
          </div>
          <div>
            <img
              src="https://painting-frontend.vercel.app/check.svg"
              alt="check"
              className="calculator_paint_radio_svg_active"
            />
            Kundtjänst öppet alla dagar i veckan
          </div>
        </div>
        <a
          href="https://hemfixarna.se/maleri-tjanstebeskrivning/"
          target={
            window.location.href.includes('hemfixarna.se') ? '_self' : '_blank'
          }
          rel="noreferrer"
        >
          *Förutsättningar och villkor
        </a>
      </div>
    </div>
  );
};

export default Summary;
